import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import ChatbotPopup from './popup/ChatbotPopup';
import ChatbotPopupV2 from './popup/ChatbotPopupV2';
import { shouldHideChatbot, areWorkspaceTypesLoaded } from './utils/urlUtils';
import { isSpeechBubbleDismissed, setSpeechBubbleDismissed } from './utils/sessionPersistence';
import '../../assets/scss/components/Agent/ChatbotButton.scss';

const USE_AGENT_V2 = process.env.REACT_APP_USE_AGENT_V2 === 'true';
const ActivePopup = USE_AGENT_V2 ? ChatbotPopupV2 : ChatbotPopup;

// Custom hook for typing animation - Pure DOM manipulation to prevent re-renders
const useTypingAnimation = (text, delay = 2000) => {
    const [bubbleState, setBubbleState] = useState({
        isVisible: false,
        isTyping: false
    });

    const typingTimeoutRef = useRef(null);
    const textElementRef = useRef(null);

    const startTyping = useCallback(() => {
        // Single state update to prevent multiple re-renders
        setBubbleState({
            isVisible: true,
            isTyping: true
        });

        // Clear any existing timeout
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }

        // Reset text element
        if (textElementRef.current) {
            textElementRef.current.textContent = '';
        }

        let currentIndex = 0;

        const animateTyping = () => {
            if (currentIndex <= text.length && textElementRef.current) {
                // Direct DOM manipulation - no React state updates
                textElementRef.current.textContent = text.slice(0, currentIndex);
                currentIndex++;

                // Schedule next character
                typingTimeoutRef.current = setTimeout(animateTyping, 100);
            } else {
                // Single state update to prevent multiple re-renders
                setBubbleState(prev => ({ ...prev, isTyping: false }));
             // Automatically close after 3 seconds
                //     setTimeout(() => {
                //      setBubbleState({
                //        isVisible: false,
                //        isTyping: false,
                //        });
                //    }, 3000);
                typingTimeoutRef.current = null;
            }
        };

        // Start the animation
        animateTyping();
    }, [text]);

    const stopTyping = useCallback(() => {
        // Single state update to prevent multiple re-renders
        setBubbleState({
            isVisible: false,
            isTyping: false
        });

        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = null;
        }

        // Clear text element
        if (textElementRef.current) {
            textElementRef.current.textContent = '';
        }
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
                typingTimeoutRef.current = null;
            }
        };
    }, []);

    return { bubbleState, startTyping, stopTyping, textElementRef };
};

const ChatbotButton = React.memo(() => {
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [bubbleDismissed, setBubbleDismissed] = useState(isSpeechBubbleDismissed);
    const pathName = useSelector((state) => state?.router?.location?.pathname || {});
    // Use refs to prevent function recreation
    const closePopupRef = useRef(() => setIsPopupOpen(false));

    // Use custom hook for typing animation
    const { bubbleState, startTyping, stopTyping, textElementRef } = useTypingAnimation("Hi I'm Spacey, How can I help you today?");

    // Redux state for workspace types - multiple sources to check
    const spaceListingTypes = useSelector((state) => state?.spaceListing?.spaceTypes || []);
    const spaceActivities = useSelector((state) => state?.activity?.data || []);
    const searchResultTypes = useSelector((state) => state?.searchResult?.spaceActivities?.data || []);
    const homePageTypes = useSelector((state) => state?.home?.homepage?.data?.spaceActivities || []);

    // Memoize the workspace types check to prevent unnecessary recalculations
    const areWorkspaceTypesLoadedMemo = useMemo(() => {
        return areWorkspaceTypesLoaded(spaceListingTypes, spaceActivities, searchResultTypes, homePageTypes);
    }, [spaceListingTypes, spaceActivities, searchResultTypes, homePageTypes]);

    // Check if workspace types are available in Redux
    const areWorkspaceTypesAvailable = useMemo(() => {
        return spaceListingTypes?.length > 0 ||
            spaceActivities?.length > 0 ||
            searchResultTypes?.length > 0 ||
            homePageTypes?.length > 0;
    }, [spaceListingTypes, spaceActivities, searchResultTypes, homePageTypes]);

    // Close speech bubble — keep launcher icon visible
    const closeBubble = useCallback(() => {
        stopTyping();
        setSpeechBubbleDismissed();
        setBubbleDismissed(true);
    }, [stopTyping]);

    // Trigger typing bubble only after data is loaded & button shown
    useEffect(() => {
        if (bubbleDismissed || !areWorkspaceTypesLoadedMemo || shouldHideChatbot()) {
            return undefined;
        }
        const bubbleTimer = setTimeout(() => {
            startTyping();
        }, 2000);

        return () => clearTimeout(bubbleTimer);
    }, [areWorkspaceTypesLoadedMemo, startTyping, pathName, bubbleDismissed]);

    // Handle URL change (close popup when hidden)
    useEffect(() => {
        const handleUrlChange = () => {
            if (isPopupOpen && shouldHideChatbot()) {
                setIsPopupOpen(false);
            }
        };
        handleUrlChange();
        window.addEventListener('popstate', handleUrlChange);
        return () => {
            window.removeEventListener('popstate', handleUrlChange);
        };
    }, [isPopupOpen]);

    const openPopup = useCallback(() => {
        stopTyping();
        setIsPopupOpen(true);
    }, [stopTyping]);

    const closePopup = useCallback(() => {
        setIsPopupOpen(false);
        if (!bubbleDismissed && areWorkspaceTypesLoadedMemo && !shouldHideChatbot()) {
            window.setTimeout(() => startTyping(), 400);
        }
    }, [bubbleDismissed, areWorkspaceTypesLoadedMemo, startTyping]);

    closePopupRef.current = closePopup;

    const togglePopup = useCallback(() => {
        if (shouldHideChatbot()) return;
        if (isPopupOpen) {
            closePopup();
        } else {
            openPopup();
        }
    }, [isPopupOpen, openPopup, closePopup]);

    // Don't render anything if hidden page OR data not loaded
    if (shouldHideChatbot() || !areWorkspaceTypesAvailable) {
        return null;
    }

    return (
        <>
            {!isPopupOpen && (
                <div
                    className="chatbot-button"
                    onClick={togglePopup}
                    role="button"
                    tabIndex={0}
                    aria-label="Open Spacey assistant"
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') togglePopup(); }}
                >
                    <div className="chatbot-icon">
                        <img
                            src={require('../../assets/images/agent/spacey-2026-3d.svg')}
                            alt="Chatbot Avatar"
                            width="28"
                            height="28"
                        />
                    </div>
                    <div className="chatbot-pulse"></div>
                </div>
            )}

            {/* Speech bubble */}
            {bubbleState.isVisible && (
                <div className="speech-bubble-container">
                    <div className="speech-bubble">
                        <span className="bubble-text" ref={textElementRef}></span>
                        {bubbleState.isTyping && <span className="typing-cursor">|</span>}
                        <button
                            className="bubble-close"
                            onClick={(e) => {
                                e.stopPropagation();
                                closeBubble();
                            }}
                            title="Close help message"
                        >
                            ×
                        </button>
                    </div>
                    <div className="speech-bubble-arrow"></div>
                </div>
            )}

            <ActivePopup isOpen={isPopupOpen} onClose={() => closePopupRef.current()} />
        </>
    );
}, (prevProps, nextProps) => {
    // Custom comparison function - always return true since we have no props
    // This prevents unnecessary re-renders when parent components re-render
    return true;
});

ChatbotButton.displayName = 'ChatbotButton';

export default ChatbotButton;
